import * as React from 'react';
import { CrucibleErrorState } from './CrucibleErrorState';
import { getPaneColour } from './PhoneStrip';
import { usePreviewInspector } from '../hooks/usePreviewInspector';
import { cn } from '../lib/cn';
import { MousePointerClick, Square, Play, RotateCw, Plus, X } from 'lucide-react';
import { Tooltip, TooltipKbd } from './ui/tooltip';
import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';
import type {
  DevSessionPairingMetadata,
  PreviewMode,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { FoundryPreviewDevState } from '@volley/crucible/foundry/state-kernel.js';

/** Mirror the cap enforced by `dev-preview-urls.ts`. Hardcoded here
 *  rather than imported because the value is renderer-side UX (button
 *  enabled/disabled state); the main-side helper is the load-bearing
 *  authority. If MAX_PREVIEW_PHONES changes, update both. Raised from
 *  4 to 6 on 2026-05-22. */
const MAX_PHONES_IN_OVERLAY = 6;

// Resizable mobile (controllers) section in the new-UI preview row layout.
// The divider between the display and the phone column adjusts this width.
const CONTROLLERS_MIN_W = 240;
const CONTROLLERS_MAX_W = 640;
const CONTROLLERS_DEFAULT_W = 320;
const CONTROLLERS_RESIZE_STEP = 24;

const PAIRING_METADATA_UNAVAILABLE = 'Unavailable';

function isPairedPreviewUi(
  previewMode: PreviewMode | undefined,
  pairing: DevSessionPairingMetadata | undefined,
): boolean {
  return previewMode === 'paired-server-only' || pairing !== undefined;
}

function formatPhoneWord(count: number): string {
  return `${count} phone${count === 1 ? '' : 's'}`;
}

/**
 * Preview surface that hosts the live display + controller iframes.
 *
 * Lifecycle:
 *   - Embedded mode is the live Workspace Preview tab surface.
 *   - Standalone mode is kept for component isolation and still supports
 *     Escape / `preview:request-close` via `onClose`.
 *
 * Accessibility:
 *   - `role="dialog"` + `aria-modal="true"` so screen readers announce
 *     it as a modal context. The chat content underneath is hidden
 *     visually by the overlay's solid background; AT users get the
 *     dialog announcement.
 *   - Focus management is intentionally minimal for the shell. Step 4's
 *     iframes will manage their own focus; we don't trap focus here
 *     (Esc to dismiss is the canonical fallback).
 */
export function PreviewOverlay(props: {
  gameName: string;
  onClose: () => void;
  /**
   * URLs for the live display + controller(s). Display is a single
   * URL (only ever one TV); controllers is an array (Track B —
   * multi-phone preview, one entry per phone pane). Missing means
   * "not yet started" and the panes show their placeholder.
   */
  displayUrl?: string;
  /**
   * Track B — one URL per phone pane. Initial length 1 once
   * `dev:start` resolves; grows via `onAddPhone`. New code should use
   * this; the legacy single-URL `controllerUrl` prop is the shim
   * below.
   */
  controllerUrls?: string[];
  /**
   * Legacy single-controller URL — shim for one release so call
   * sites that haven't migrated to `controllerUrls` keep working.
   * When `controllerUrls` is undefined, this is folded into a
   * single-entry array; when both are supplied, `controllerUrls`
   * wins.
   *
   * @deprecated use `controllerUrls` instead.
   */
  controllerUrl?: string;
  pairing?: DevSessionPairingMetadata;
  /** Weekend pairing layout when `paired-server-only` even if tunnel
   *  metadata is absent (e.g. dev:start reuse without main-side pairing). */
  previewMode?: PreviewMode;
  /**
   * Step 5 — true while `dev:start` is in flight. The body shows a
   * centred status card. False when the URLs are populated (we
   * mount iframes), or when nothing is happening yet (panes show
   * their idle placeholder).
   */
  loading?: boolean;
  loadingLabel?: string;
  loadingDetail?: string;
  /**
   * Step 6 — populated when `dev:start` rejected. Overlay renders a
   * centred error card with the structured CrucibleError plus two
   * recovery buttons:
   *   - "Send to chat for help" fires `onSendErrorToChat`, which
   *     the parent translates into a chat user-message asking the
   *     agent to /diagnose and fix. Closing the overlay is the
   *     parent's call (recommended: yes — drop the user back into
   *     the chat thread where the response will land).
   *   - "Dismiss" fires `onDismissError`, which clears the error
   *     state but keeps the overlay open so the user can retry the
   *     launch from the same place.
   */
  error?: SerialisedError | null;
  onSendErrorToChat?: (error: SerialisedError) => void;
  onDismissError?: () => void;
  onRetryPreview?: () => void;
  /**
   * Issue #643 — monotonic counter from `usePreviewSession`. Folded
   * into the iframe `key` props so a bump (from the user clicking
   * "Reload preview" or a successful `preview:restart`) forces
   * React to drop the old iframe and mount a fresh one against the
   * same URL. The cheapest equivalent of a hard-refresh given the
   * panes are plain `<iframe>` elements, not Electron `<webview>`.
   * Defaults to 0 — older call sites that don't pass the prop get
   * the legacy single-mount behaviour for free.
   */
  reloadKey?: number;
  /**
   * Issue #643 — Reload + Restart preview handlers. Rendered as an
   * overflow menu in the overlay header so the affordances are
   * reachable WHILE the overlay is mounted (Codex review #1 HIGH).
   * The chat-header menu is invisible underneath the `z-50` overlay,
   * so without this in-overlay surface a wonky preview locked the
   * user out of the recovery actions exactly when they needed them
   * most. When omitted (legacy callers, or no session ready), the
   * menu is hidden — the prop is fully optional.
   */
  onReloadPreview?: () => void;
  onRestartPreview?: () => void;
  onStopPreview?: () => void;
  /** Whether the actions menu's items should be enabled (mirrors
   *  the chat-header menu's `previewMenuEnabled` gate). */
  previewMenuEnabled?: boolean;
  /**
   * Track B — fired when the user clicks "+ Add Phone". The
   * parent wires this to `usePreviewSession.addPhone()`. When
   * omitted, the button is hidden entirely (legacy callers, or no
   * session ready).
   */
  onAddPhone?: () => void;
  /**
   * Track B — fired when the user clicks "− Remove Phone".
   * Parent wires this to `usePreviewSession.removePhone()`. When
   * omitted, the button is hidden.
   */
  onRemovePhone?: () => void;
  /** Remove a specific phone by index (per-screen ✕ on the phone strip). */
  onRemovePhoneAt?: (index: number) => void | Promise<boolean>;
  maxControllerSlots?: number;
  previewPhoneCapOverride?: number | null;
  onPreviewPhoneCapChange?: (value: number | null) => void | Promise<void>;
  playerCountLabel?: string;
  playerCountSource?: 'quickAnswers' | 'slotAnswers' | 'unknown';
  diagnostics?: FoundryPreviewDevState | null;
  /** @deprecated PreviewOverlay always renders the focused-phone layout. */
  newUi?: boolean;
  /**
   * When true, the overlay renders in-flow (`relative`, fills its parent)
   * instead of as a fullscreen fixed-position modal. Used by the
   * new-UI three-zone build view, which embeds the preview as the main
   * content area alongside a docked chat + activity panel. In embedded
   * mode the overlay is NOT a modal: it skips the window-level Escape /
   * `preview:request-close` close pipeline and the mount-focus bias (an
   * embedded surface must not steal focus or hijack Escape from the rest
   * of the workspace). */
  embedded?: boolean;
  /**
   * Live-preview (§3.G) PARENT→iframe construction-event bridge. A ref
   * attached to the live DISPLAY `<iframe>` so the caller (the Workspace's
   * `usePreviewConstructionBridge`) can `contentWindow.postMessage` the
   * construction-event + build-active records across the parent→iframe
   * boundary. Purely a wiring seam — the overlay just forwards the ref onto
   * the display iframe in whichever layout renders. Omitted by callers that
   * don't run the bridge (flag-OFF / legacy) → no behavioural change. */
  displayIframeRef?: React.Ref<HTMLIFrameElement>;
  onDisplayIframeLoad?: (url: string) => void;
  /**
   * Select-to-chat P1 (`docs/design/preview-select-to-chat-prd.md`)
   * — receives the cropped area selection (PNG file + formatted
   * `crucible-selection` context block) when the user clicks
   * "Add to chat" on a framed selection. The caller (Workspace)
   * routes it through the resize → `user/add-attachment` pipeline.
   * Omitted (legacy callers, crucible-web without the capture IPC)
   * → the Select affordance never renders. New-UI layout only.
   */
  onAddSelectionToChat?: (payload: { file: File; contextBlock: string }) => void;
  onSelectionError?: (message: string) => void;
  /** Where a captured selection flies — see `usePreviewInspector`'s `flyTo`. */
  flyTo?: 'composer' | 'chat-tab';
}): React.JSX.Element {
  const {
    gameName,
    onClose,
    displayUrl,
    controllerUrls: propControllerUrls,
    controllerUrl,
    pairing,
    previewMode,
    loading,
    loadingLabel,
    loadingDetail,
    error,
    onSendErrorToChat,
    onDismissError,
    onRetryPreview,
    reloadKey,
    onReloadPreview,
    onRestartPreview,
    onStopPreview,
    previewMenuEnabled,
    onAddPhone,
    onRemovePhone,
    onRemovePhoneAt,
    maxControllerSlots,
    previewPhoneCapOverride,
    onPreviewPhoneCapChange,
    playerCountLabel,
    playerCountSource,
    diagnostics,
    embedded,
    displayIframeRef,
    onAddSelectionToChat,
    onSelectionError,
    flyTo,
  } = props;

  // Track B — normalize the two URL props into a single array. New
  // callers pass `controllerUrls` (array, preferred); legacy callers
  // pass `controllerUrl` (single, deprecated shim). When neither is
  // supplied the array is empty (URLs not yet known → placeholder).
  //
  // Both supplied: `controllerUrls` wins. The shim's whole purpose
  // is "we ship the array; legacy code keeps working" — once a
  // caller migrates to passing the array we ignore the singular.
  const controllerUrls = React.useMemo<string[]>(() => {
    if (propControllerUrls && propControllerUrls.length > 0) return propControllerUrls;
    if (controllerUrl) return [controllerUrl];
    return [];
  }, [propControllerUrls, controllerUrl]);

  // Track B — focused phone state. `null` = default mode (TV fills,
  // phones in strip below). Otherwise the index into `controllerUrls`
  // of the phone currently promoted to the focused pane next to the
  // TV. Resets to null when the focused phone is removed (effect
  // below).
  const [focusedPaneIndex, setFocusedPaneIndex] = React.useState<number | null>(null);

  // If the focused phone is removed (or the array shrinks past it),
  // drop focus so we don't render a bogus focused-mode layout.
  React.useEffect(() => {
    if (focusedPaneIndex !== null && focusedPaneIndex >= controllerUrls.length) {
      setFocusedPaneIndex(null);
    }
  }, [controllerUrls.length, focusedPaneIndex]);

  // Issue #530 — keyboard exit affordance. Three-pronged so the
  // iframe's own key handlers can't swallow Escape:
  //
  //   1. window-level `keydown` listener registered in CAPTURE phase.
  //      Catches Escape when focus is on the overlay chrome (header,
  //      buttons) or anywhere the iframe doesn't capture it.
  //   2. A `tabIndex={-1}` on the overlay container with `.focus()` on
  //      mount, so initial focus lands on the overlay rather than
  //      bouncing straight into an iframe. Belt-and-braces for (1).
  //   3. A main-process `before-input-event` listener on the
  //      BrowserWindow webContents that pushes `preview:request-close`
  //      on every Escape keydown — regardless of which sub-element
  //      (iframe / button / outer chrome) has focus. This is the
  //      load-bearing path for the iframe-focus case: an iframe whose
  //      game `preventDefault`s Escape in its own keydown handler
  //      would silently swallow the renderer-side listener; the main-
  //      process listener fires in the embedder webContents BEFORE
  //      the iframe sees it, so we always get the chance to close.
  //
  // Cleanup on unmount tears down all three so the dialog's keydown
  // contract doesn't outlive the dialog.
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Issue #530 follow-up (Codex review #2): the initial-focus
  // call MUST live in a mount-only effect, not in the keydown /
  // request-close subscription effect below. If the focus call shared
  // a useEffect with `[onClose]` deps, every ancestor re-render with a
  // fresh handler would steal focus back from whatever child currently
  // held it (e.g. the controller iframe, a focused button inside the
  // overlay). The bias-on-mount behaviour we actually want runs exactly
  // once when the overlay opens; subsequent re-renders are no-ops.
  // NOTE: intentional mount-only focus bias (empty deps). The `react-hooks`
  // ESLint plugin is not enabled in this repo's flat config, so a
  // `react-hooks/exhaustive-deps` disable directive would itself error
  // ("rule not found"); the empty-deps intent is documented here instead.
  React.useEffect(() => {
    // Embedded mode is not a modal — it must not grab focus on mount
    // (that would yank focus away from the chat composer / activity
    // panel sharing the build view). `embedded` is fixed for the
    // lifetime of a given mount, so reading it in this mount-only
    // effect is safe.
    if (embedded) return;
    containerRef.current?.focus();
  }, []);

  // Codex review #3 LOW — pair the renderer keydown path with a
  // single-shot suppression flag for the main-process
  // `preview:request-close` push. The renderer keydown fires
  // synchronously (capture phase), but the IPC push arrives async
  // (`before-input-event` → main → renderer event loop). If the
  // user's Esc closed the menu in the renderer first, the menu
  // unmounts BEFORE the async push callback runs, so the push
  // callback's `querySelector` returns null and closes the
  // overlay anyway. Setting this flag from the renderer's keydown
  // (when it observed menu-open) tells the push callback to
  // consume-and-skip the next fire for this keystroke. The flag
  // auto-resets after a short window (paranoia against a future
  // bug where the push never arrives — we don't want to swallow a
  // genuine Esc forever).
  //
  // Codex review #4 LOW — track the timeout id in a ref so rapid
  // Esc / reopen cycles don't pile up dangling timers. An older
  // timer firing after a NEWER suppression was set would clear
  // the newer window and let the later async push close the
  // overlay. Clear the previous timer before scheduling a new one
  // and on unmount.
  const suppressNextRequestCloseRef = React.useRef(false);
  const suppressClearTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    // Embedded mode is not a modal, so it owns no global close
    // affordance — Escape and the main-process `preview:request-close`
    // push belong to the rest of the workspace. Skip the whole
    // pipeline so an embedded build-view preview never closes itself.
    if (embedded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Codex review #2 LOW — Esc with the actions menu open
        // must close ONLY the menu, not the whole overlay. The
        // menu renders its dropdown with `data-testid=
        // "preview-actions-menu"` only when open; querying the
        // document at keystroke time tells us whether the menu
        // owns this Esc. Same pattern as a focus-trap "is anything
        // claiming Esc?" check, just keyed on a DOM presence
        // probe rather than a portal context. Cheap (O(1)
        // `querySelector` on a class-style attribute selector)
        // and side-effect-free.
        // Select-to-chat P1 — an active select-area overlay owns Esc
        // (clear selection → exit select mode) exactly like the menu
        // owns it when open. Same DOM-presence probe pattern; the
        // overlay's own window keydown listener (added after this one,
        // so it runs second in the shared capture phase) performs the
        // actual handling.
        const menuOpen =
          !!document.querySelector('[data-testid="preview-actions-menu"]') ||
          !!document.querySelector('[data-testid="preview-select-instructions"]');
        if (menuOpen) {
          // Let the menu's own keydown listener handle the close.
          // We don't stopPropagation here — the menu's listener
          // already swallows the keystroke via
          // `stopImmediatePropagation()`. The check above is
          // belt-and-braces against listener-order surprises.
          //
          // Codex review #3 LOW — set the suppression flag so the
          // async main-process `preview:request-close` push for
          // THIS keystroke is consumed-and-skipped. Without this,
          // the menu unmounts (clearing the DOM-probe signal)
          // before the push callback runs, and the overlay closes
          // anyway. Auto-clear after 500ms so a swallowed-push
          // bug never leaves the suppression in place forever.
          //
          // Codex review #4 LOW — cancel any pending older timer
          // before scheduling a new one. Without this, rapid Esc/
          // reopen cycles would queue overlapping timers and an
          // older one could fire AFTER a newer suppression was
          // set, clearing it prematurely and letting the next
          // async push close the overlay.
          suppressNextRequestCloseRef.current = true;
          if (suppressClearTimerRef.current !== null) {
            clearTimeout(suppressClearTimerRef.current);
          }
          suppressClearTimerRef.current = setTimeout(() => {
            suppressNextRequestCloseRef.current = false;
            suppressClearTimerRef.current = null;
          }, 500);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    // Capture phase — fires before the iframe / inner buttons see it.
    window.addEventListener('keydown', onKeyDown, true);
    // Issue #530 — main-process Escape pipeline. The push channel
    // fires on every Escape keydown at the BrowserWindow level
    // (`before-input-event`). Treating the push as a synonym for
    // the window-level Escape — both route through the same
    // `onClose`. Multiple firings are idempotent because `onClose`
    // flips parent state from open → closed; a second call no-ops.
    //
    // Codex review #2 LOW + #3 LOW — menu-owns-Esc composition.
    // Two complementary signals:
    //   1. `suppressNextRequestCloseRef` — set by the renderer
    //      keydown path the moment it observes menu-open Esc.
    //      Race-free against the menu's React-unmount because the
    //      flag is set synchronously in the keydown phase, BEFORE
    //      the menu's own setState fires.
    //   2. `querySelector` DOM probe — fallback for the case where
    //      the keydown listener didn't fire (e.g. main-process
    //      push for a keystroke the renderer never saw, like a
    //      focused iframe ate the keydown). The menu is still
    //      mounted in that case, so the probe correctly returns
    //      true.
    const unsubRequestClose = window.crucible?.on?.('preview:request-close', () => {
      if (suppressNextRequestCloseRef.current) {
        suppressNextRequestCloseRef.current = false;
        // Codex review #4 LOW — clear the pending auto-clear
        // timer when consuming suppression. Without this, the
        // timer would still fire and (harmlessly here, since
        // the flag is already false) leak a dangling timer ref.
        // Clearing keeps the ref well-defined for the next
        // schedule.
        if (suppressClearTimerRef.current !== null) {
          clearTimeout(suppressClearTimerRef.current);
          suppressClearTimerRef.current = null;
        }
        return;
      }
      const menuOpen =
        !!document.querySelector('[data-testid="preview-actions-menu"]') ||
        // Select-to-chat P1 — select-area overlay owns Esc (see the
        // keydown path above for the rationale).
        !!document.querySelector('[data-testid="preview-select-instructions"]');
      if (menuOpen) return;
      onClose();
    });
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      if (typeof unsubRequestClose === 'function') {
        unsubRequestClose();
      }
      // Codex review #4 LOW — clear any pending suppression timer
      // on unmount so an overlay closed while a suppression timer
      // is in flight doesn't fire a setTimeout callback against
      // unmounted refs.
      if (suppressClearTimerRef.current !== null) {
        clearTimeout(suppressClearTimerRef.current);
        suppressClearTimerRef.current = null;
      }
    };
  }, [onClose, embedded]);

  return (
    <NewUiPreviewOverlayLayout
      containerRef={containerRef}
      embedded={!!embedded}
      gameName={gameName}
      displayUrl={displayUrl}
      controllerUrls={controllerUrls}
      pairing={pairing}
      previewMode={previewMode}
      loading={!!loading}
      loadingLabel={loadingLabel}
      loadingDetail={loadingDetail}
      error={error ?? null}
      onSendErrorToChat={onSendErrorToChat}
      onDismissError={onDismissError}
      onRetryPreview={onRetryPreview}
      onAddPhone={onAddPhone}
      onRemovePhone={onRemovePhone}
      onRemovePhoneAt={onRemovePhoneAt}
      maxControllerSlots={maxControllerSlots}
      previewPhoneCapOverride={previewPhoneCapOverride}
      onPreviewPhoneCapChange={onPreviewPhoneCapChange}
      playerCountLabel={playerCountLabel}
      playerCountSource={playerCountSource}
      diagnostics={diagnostics}
      focusedPaneIndex={focusedPaneIndex}
      setFocusedPaneIndex={setFocusedPaneIndex}
      onStopPreview={onStopPreview}
      onReloadPreview={onReloadPreview}
      onRestartPreview={onRestartPreview}
      previewMenuEnabled={previewMenuEnabled}
      reloadKey={reloadKey ?? 0}
      displayIframeRef={displayIframeRef}
      onDisplayIframeLoad={props.onDisplayIframeLoad}
      onAddSelectionToChat={onAddSelectionToChat}
      onSelectionError={onSelectionError}
      flyTo={flyTo}
    />
  );
}

// ─── Focused preview layout components ───────────────────────────────────────
// The legacy fullscreen layout has been removed; PreviewOverlay now always uses
// the focused-phone preview surface.

function NewUiPreviewOverlayLayout(props: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** In-flow (embedded) vs fullscreen-modal rendering — see
   *  `PreviewOverlay`'s `embedded` prop. */
  embedded: boolean;
  gameName: string;
  displayUrl?: string;
  controllerUrls: string[];
  pairing?: DevSessionPairingMetadata;
  previewMode?: PreviewMode;
  loading: boolean;
  loadingLabel?: string;
  loadingDetail?: string;
  error: SerialisedError | null;
  onSendErrorToChat?: (error: SerialisedError) => void;
  onDismissError?: () => void;
  onRetryPreview?: () => void;
  onAddPhone?: () => void;
  onRemovePhone?: () => void;
  /** Remove a specific phone by index (per-screen ✕ on the phone strip). */
  onRemovePhoneAt?: (index: number) => void | Promise<boolean>;
  maxControllerSlots?: number;
  previewPhoneCapOverride?: number | null;
  onPreviewPhoneCapChange?: (value: number | null) => void | Promise<void>;
  playerCountLabel?: string;
  playerCountSource?: 'quickAnswers' | 'slotAnswers' | 'unknown';
  diagnostics?: FoundryPreviewDevState | null;
  focusedPaneIndex: number | null;
  setFocusedPaneIndex: (idx: number | null) => void;
  onStopPreview?: () => void;
  onReloadPreview?: () => void;
  onRestartPreview?: () => void;
  previewMenuEnabled?: boolean;
  reloadKey: number;
  displayIframeRef?: React.Ref<HTMLIFrameElement>;
  onDisplayIframeLoad?: (url: string) => void;
  onAddSelectionToChat?: (payload: { file: File; contextBlock: string }) => void;
  onSelectionError?: (message: string) => void;
  flyTo?: 'composer' | 'chat-tab';
}): React.JSX.Element {
  const {
    containerRef,
    embedded,
    gameName,
    displayUrl,
    controllerUrls,
    pairing,
    previewMode,
    loading,
    loadingLabel,
    loadingDetail,
    error,
    onSendErrorToChat,
    onDismissError,
    onRetryPreview,
    onAddPhone,
    onRemovePhone,
    onRemovePhoneAt,
    maxControllerSlots,
    previewPhoneCapOverride,
    onPreviewPhoneCapChange,
    playerCountLabel,
    playerCountSource,
    diagnostics,
    focusedPaneIndex,
    setFocusedPaneIndex,
    onStopPreview,
    onReloadPreview,
    onRestartPreview,
    previewMenuEnabled,
    reloadKey,
    displayIframeRef,
    onDisplayIframeLoad,
    onAddSelectionToChat,
    onSelectionError,
    flyTo,
  } = props;
  return (
    // Issue #804 — `app-region-no-drag` on the ENTIRE overlay container so
    // the whole surface opts out of Electron's drag-region hit-testing.
    // Embedded: in-flow `relative` surface that fills its parent (the
    // build view's main column). Fullscreen: `fixed` modal anchored below
    // the 76px header. Embedded is not a modal — drop `aria-modal` and use
    // a plain region role.
    <div
      ref={containerRef}
      tabIndex={-1}
      className={
        embedded
          ? 'app-region-no-drag relative flex h-full w-full flex-col bg-crucible-bg outline-none'
          : 'app-region-no-drag fixed inset-x-0 bottom-0 top-[76px] z-50 flex flex-col bg-crucible-bg outline-none'
      }
      role={embedded ? 'region' : 'dialog'}
      aria-modal={embedded ? undefined : 'true'}
      aria-label={`Preview — ${gameName}`}
      data-testid="preview-overlay"
    >
      <div className="flex flex-1 flex-col overflow-hidden" data-testid="preview-overlay-body">
        <NewUiPreviewBody
          embedded={embedded}
          displayUrl={displayUrl}
          controllerUrls={controllerUrls}
          pairing={pairing}
          previewMode={previewMode}
          loading={loading}
          loadingLabel={loadingLabel}
          loadingDetail={loadingDetail}
          error={error}
          onSendErrorToChat={onSendErrorToChat}
          onDismissError={onDismissError}
          onRetryPreview={onRetryPreview}
          onAddPhone={onAddPhone}
          onRemovePhone={onRemovePhone}
          onRemovePhoneAt={onRemovePhoneAt}
          maxControllerSlots={maxControllerSlots}
          previewPhoneCapOverride={previewPhoneCapOverride}
          onPreviewPhoneCapChange={onPreviewPhoneCapChange}
          playerCountLabel={playerCountLabel}
          playerCountSource={playerCountSource}
          diagnostics={diagnostics}
          focusedPaneIndex={focusedPaneIndex}
          setFocusedPaneIndex={setFocusedPaneIndex}
          onStopPreview={onStopPreview}
          onReloadPreview={onReloadPreview}
          onRestartPreview={onRestartPreview}
          previewMenuEnabled={previewMenuEnabled}
          reloadKey={reloadKey}
          displayIframeRef={displayIframeRef}
          onDisplayIframeLoad={onDisplayIframeLoad}
          onAddSelectionToChat={onAddSelectionToChat}
          onSelectionError={onSelectionError}
          flyTo={flyTo}
        />
      </div>
    </div>
  );
}

function NewUiPreviewBody(props: {
  /** New UI uses one focused-phone mental model everywhere:
   *  display left, focused phone/control column right at desktop
   *  widths, stacked only when the window is narrow. */
  embedded?: boolean;
  displayUrl?: string;
  controllerUrls: string[];
  pairing?: DevSessionPairingMetadata;
  previewMode?: PreviewMode;
  loading: boolean;
  loadingLabel?: string;
  loadingDetail?: string;
  error: SerialisedError | null;
  onSendErrorToChat?: (error: SerialisedError) => void;
  onDismissError?: () => void;
  onRetryPreview?: () => void;
  onAddPhone?: () => void;
  onRemovePhone?: () => void;
  /** Remove a specific phone by index (per-screen ✕ on the phone strip). */
  onRemovePhoneAt?: (index: number) => void | Promise<boolean>;
  maxControllerSlots?: number;
  previewPhoneCapOverride?: number | null;
  onPreviewPhoneCapChange?: (value: number | null) => void | Promise<void>;
  playerCountLabel?: string;
  playerCountSource?: 'quickAnswers' | 'slotAnswers' | 'unknown';
  diagnostics?: FoundryPreviewDevState | null;
  focusedPaneIndex: number | null;
  setFocusedPaneIndex: (idx: number | null) => void;
  onStopPreview?: () => void;
  onReloadPreview?: () => void;
  onRestartPreview?: () => void;
  previewMenuEnabled?: boolean;
  reloadKey: number;
  displayIframeRef?: React.Ref<HTMLIFrameElement>;
  onDisplayIframeLoad?: (url: string) => void;
  onAddSelectionToChat?: (payload: { file: File; contextBlock: string }) => void;
  onSelectionError?: (message: string) => void;
  flyTo?: 'composer' | 'chat-tab';
}): React.JSX.Element {
  // Select-to-chat — one global Pause/Resume that puts EVERY preview
  // screen (TV + controllers) into unified select mode. The hook
  // injects + arms the inspector in each iframe found within
  // `bodyRef`; the inspector owns all in-screen interaction and posts
  // captured selections back through `onAddSelectionToChat`.
  const bodyRef = React.useRef<HTMLDivElement | null>(null);

  // Drag-resizable mobile (controllers) section — same behaviour as the
  // chat | preview divider, but the sized pane is on the RIGHT. Applied
  // via a CSS variable so it only binds in the row layout (`lg:`); the
  // stacked (narrow) layout keeps full-width panes. Local UI state.
  const [controllersWidth, setControllersWidth] = React.useState(CONTROLLERS_DEFAULT_W);
  const clampControllersWidth = React.useCallback((width: number): number => {
    const el = bodyRef.current;
    const fromBody = el ? el.getBoundingClientRect().width - 320 : Infinity; // keep ≥320 for the display
    const max = Math.max(CONTROLLERS_MIN_W, Math.min(CONTROLLERS_MAX_W, fromBody));
    return Math.min(max, Math.max(CONTROLLERS_MIN_W, width));
  }, []);
  const handleControllersResize = React.useCallback(
    (clientX: number) => {
      const el = bodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      // Controllers pane is on the right: width = body right edge − pointer x.
      setControllersWidth(clampControllersWidth(rect.right - clientX));
    },
    [clampControllersWidth],
  );
  const handleControllersDividerPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      const move = (e: PointerEvent) => handleControllersResize(e.clientX);
      const up = (e: PointerEvent) => {
        handle.releasePointerCapture(e.pointerId);
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
    },
    [handleControllersResize],
  );
  const handleControllersDividerKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Controllers on the right: ArrowLeft grows it, ArrowRight shrinks.
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setControllersWidth((w) => clampControllersWidth(w + CONTROLLERS_RESIZE_STEP));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setControllersWidth((w) => clampControllersWidth(w - CONTROLLERS_RESIZE_STEP));
      }
    },
    [clampControllersWidth],
  );

  const selectEnabled =
    !!props.onAddSelectionToChat &&
    !!props.displayUrl &&
    typeof window !== 'undefined' &&
    typeof window.crucible?.invoke === 'function';
  const noopAddSelection = React.useCallback(
    (_payload: { file: File; contextBlock: string }) => undefined,
    [],
  );
  const inspector = usePreviewInspector({
    containerRef: bodyRef,
    enabled: selectEnabled,
    onAddSelectionToChat: props.onAddSelectionToChat ?? noopAddSelection,
    onSelectionError: props.onSelectionError,
    flyTo: props.flyTo,
  });

  // Reload/restart remounts the iframes (the injected inspectors are
  // gone with the old frames) — drop out of pause mode.
  const inspectorRef = React.useRef(inspector);
  inspectorRef.current = inspector;
  const previewIdentityRef = React.useRef({
    displayUrl: props.displayUrl,
    reloadKey: props.reloadKey,
  });
  React.useEffect(() => {
    const previous = previewIdentityRef.current;
    previewIdentityRef.current = {
      displayUrl: props.displayUrl,
      reloadKey: props.reloadKey,
    };
    if (previous.displayUrl !== props.displayUrl || previous.reloadKey !== props.reloadKey) {
      inspectorRef.current.exit();
    }
  }, [props.reloadKey, props.displayUrl]);

  if (props.error && !props.displayUrl) {
    return (
      <PreviewErrorCard
        error={props.error}
        onSendToChat={props.onSendErrorToChat}
        onDismiss={props.onDismissError}
        onRetryPreview={props.onRetryPreview}
        showBackButton={!props.embedded}
      />
    );
  }
  return (
    <div
      ref={bodyRef}
      className="relative flex h-full w-full flex-col lg:flex-row"
      data-preview-layout="focused-phone"
      data-preview-mode={props.embedded ? 'embedded' : 'fullscreen'}
      data-testid="preview-overlay-placeholder"
      // Drives the mobile (controllers) section width in the row layout
      // only; the stacked layout ignores it (panes go full-width).
      style={
        {
          '--preview-ctrl-w': `${clampControllersWidth(controllersWidth)}px`,
        } as React.CSSProperties
      }
    >
      {/* TV column: the display + the small mobile screens as ONE centred
          group (strip ~16px under the TV). The display-area is the size
          container; the 16:9 box letterboxes against the height left after
          reserving room for the strip, so the two stay tight. Toolbar +
          select instructions live here too, so they centre on the TV. */}
      {(() => {
        const showStrip =
          !isPairedPreviewUi(props.previewMode, props.pairing) && props.controllerUrls.length > 0;
        return (
          <div className="flex min-h-0 min-w-0 flex-[1080] flex-col overflow-hidden">
            <div
              data-testid="preview-pane-display"
              className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-hidden p-4"
              style={{ containerType: 'size' }}
            >
              {/* TV stays vertically centred in the display-area (full
                  height, no strip reserve). */}
              <DisplayPane
                url={props.displayUrl}
                reloadKey={props.reloadKey}
                loading={props.loading}
                displayIframeRef={props.displayIframeRef}
                onDisplayIframeLoad={props.onDisplayIframeLoad}
                reservePx={0}
              />
              {/* Phone strip is pinned to the bottom (absolute) so it sits
                  below the TV WITHOUT shifting the TV's centred position. */}
              {showStrip && (
                <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center px-4">
                  <div className="pointer-events-auto">
                    <PreviewPhoneStrip
                      urls={props.controllerUrls}
                      focusedIndex={Math.min(
                        props.focusedPaneIndex ?? 0,
                        props.controllerUrls.length - 1,
                      )}
                      setFocusedPaneIndex={props.setFocusedPaneIndex}
                      onAddPhone={props.onAddPhone}
                      onRemovePhoneAt={props.onRemovePhoneAt}
                      maxControllerSlots={Math.max(
                        1,
                        Math.min(
                          MAX_PHONES_IN_OVERLAY,
                          Math.floor(props.maxControllerSlots ?? MAX_PHONES_IN_OVERLAY),
                        ),
                      )}
                    />
                  </div>
                </div>
              )}
              {/* Floating toolbar at the TOP of the TV, with the select
                  cheat-sheet directly beneath it while selecting. */}
              <div className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 flex-col items-center gap-2">
                <PreviewToolbar
                  selectEnabled={selectEnabled}
                  selectActive={inspector.paused}
                  onToggleSelect={() => {
                    if (inspector.paused) inspector.exit();
                    else void inspector.enter();
                  }}
                  running={props.previewMenuEnabled === true}
                  onStopPreview={props.onStopPreview}
                  onRestartPreview={props.onRestartPreview}
                  onReloadPreview={props.onReloadPreview}
                />
                {inspector.paused && <SelectInstructions />}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Resizable divider between the display and the mobile section —
          row layout only (hidden when stacked). Drag or arrow-key it. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the mobile preview section"
        tabIndex={0}
        onPointerDown={handleControllersDividerPointerDown}
        onKeyDown={handleControllersDividerKeyDown}
        data-testid="preview-controllers-divider"
        className={cn(
          // A thin, full-height divider LINE (not a grip icon) between the TV
          // and the focused-phone section. Quiet hairline at rest; brightens on
          // hover and brand-tints on keyboard focus to signal it's draggable.
          // Horizontal margin keeps it off the TV and the phone screen.
          'group relative hidden shrink-0 cursor-col-resize bg-white/10 outline-none transition-colors lg:mx-2 lg:block lg:w-px',
          'hover:bg-white/30 focus-visible:bg-crucible-accent',
          'before:absolute before:-inset-x-1.5 before:inset-y-0 before:content-[""]',
        )}
      />
      <ControllersRow
        urls={props.controllerUrls}
        pairing={props.pairing}
        previewMode={props.previewMode}
        reloadKey={props.reloadKey}
        loading={props.loading}
        embedded={props.embedded}
        focusedPaneIndex={props.focusedPaneIndex}
        setFocusedPaneIndex={props.setFocusedPaneIndex}
        onAddPhone={props.onAddPhone}
        onRemovePhone={props.onRemovePhone}
        maxControllerSlots={props.maxControllerSlots}
        previewPhoneCapOverride={props.previewPhoneCapOverride}
        onPreviewPhoneCapChange={props.onPreviewPhoneCapChange}
        playerCountLabel={props.playerCountLabel}
        playerCountSource={props.playerCountSource}
        diagnostics={props.diagnostics}
      />
    </div>
  );
}

/**
 * Floating preview toolbar: [Select] | [Stop/Play] | [Reload]. Sits
 * below the TV, centred over the preview body. Replaces the
 * header's top-right lifecycle cluster (moved here per design).
 *
 *  - Select — toggles unified select mode (hover/click an element,
 *    drag an area; the inspector draws the highlight + Add-to-chat).
 *  - Stop/Play — Stop halts the preview server; the same button then
 *    becomes Play to relaunch it (Restart is folded into this, so the
 *    standalone Restart button is gone).
 *  - Reload — refresh the iframes without restarting the server.
 *
 * Each control reveals its purpose on hover/focus via a neutral
 * tooltip (same shape as the attachment-info popover, neutral colour).
 */
function PreviewToolbar(props: {
  selectEnabled: boolean;
  selectActive: boolean;
  onToggleSelect: () => void;
  running: boolean;
  onStopPreview?: () => void;
  onRestartPreview?: () => void;
  onReloadPreview?: () => void;
}): React.JSX.Element | null {
  const hasLifecycle = !!props.onStopPreview || !!props.onRestartPreview || !!props.onReloadPreview;

  // ⌘/Ctrl+Shift shortcuts, active only while the toolbar is mounted
  // (i.e. the preview is visible). Modifier combos never clash with
  // chat typing. `mod`/`shortcut` labels adapt to the platform.
  const isMac =
    typeof navigator !== 'undefined' && /mac/i.test(navigator.platform || navigator.userAgent);
  // Spelled-out modifiers with `+` separators read far better than the
  // ⌘⇧ glyphs (⇧ in particular is hard to parse at chip size). Cmd on
  // macOS, Ctrl elsewhere; Shift is the same word on both.
  const primaryMod = isMac ? 'Cmd' : 'Ctrl';
  const shortcutFor = (key: string) => `${primaryMod} + Shift + ${key}`;
  const sc = { select: shortcutFor('S'), play: shortcutFor('P'), reload: shortcutFor('R') };

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if (e.code === 'KeyS' && props.selectEnabled) {
        e.preventDefault();
        props.onToggleSelect();
      } else if (e.code === 'KeyP') {
        if (props.running && props.onStopPreview) {
          e.preventDefault();
          props.onStopPreview();
        } else if (!props.running && props.onRestartPreview) {
          e.preventDefault();
          props.onRestartPreview();
        }
      } else if (e.code === 'KeyR' && props.running && props.onReloadPreview) {
        e.preventDefault();
        props.onReloadPreview();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [
    props.selectEnabled,
    props.onToggleSelect,
    props.running,
    props.onStopPreview,
    props.onRestartPreview,
    props.onReloadPreview,
  ]);

  if (!props.selectEnabled && !hasLifecycle) return null;
  return (
    <div
      className="app-region-no-drag flex items-center gap-1.5 rounded-full p-1.5 backdrop-blur-[8px]"
      data-testid="preview-toolbar"
      // Unified with the main mode-pill bar surface (same bg + hairline
      // ring tokens); keeps a drop shadow since it floats over the preview.
      style={{
        background: 'var(--surface-mode-pill-bar)',
        boxShadow: 'var(--surface-mode-pill-bar-ring), 0 6px 18px rgba(0, 0, 0, 0.35)',
      }}
    >
      {(props.onStopPreview || props.onRestartPreview) &&
        (props.running ? (
          <PreviewToolButton
            label="Stop the preview"
            shortcut={sc.play}
            onClick={props.onStopPreview}
            disabled={!props.onStopPreview}
            testid="preview-tool-stop"
            ariaLabel="Stop preview"
          >
            <Square size={18} strokeWidth={1.75} />
          </PreviewToolButton>
        ) : (
          <PreviewToolButton
            label="Play the preview"
            shortcut={sc.play}
            onClick={props.onRestartPreview}
            disabled={!props.onRestartPreview}
            testid="preview-tool-play"
            ariaLabel="Play preview"
          >
            <Play size={18} strokeWidth={1.75} />
          </PreviewToolButton>
        ))}

      {props.onReloadPreview && (
        <PreviewToolButton
          label="Reload the preview"
          shortcut={sc.reload}
          onClick={props.onReloadPreview}
          disabled={!props.running}
          testid="preview-tool-reload"
          ariaLabel="Reload preview"
        >
          <RotateCw size={18} strokeWidth={1.75} />
        </PreviewToolButton>
      )}

      {props.selectEnabled && hasLifecycle && <ToolbarDivider />}

      {props.selectEnabled && (
        <PreviewToolButton
          label={props.selectActive ? 'Stop selecting' : 'Select an element or area to add to Chat'}
          shortcut={sc.select}
          active={props.selectActive}
          onClick={props.onToggleSelect}
          testid="preview-tool-select"
          ariaLabel="Select"
        >
          <MousePointerClick size={18} strokeWidth={1.75} />
        </PreviewToolButton>
      )}
    </div>
  );
}

/** Key cheat-sheet shown as a plain text line directly on the preview
 *  background (no chip/pill) while selecting. */
function SelectInstructions(): React.JSX.Element {
  const items: Array<{ key: string; label: string }> = [
    { key: 'Click', label: 'select element' },
    { key: 'Double-click', label: 'go deeper' },
    { key: 'Drag', label: 'select area' },
    { key: 'Esc', label: 'step out' },
  ];
  return (
    <p
      className="m-0 text-center text-[12px] text-white/55"
      data-testid="preview-select-instructions"
    >
      {items.map((it, i) => (
        <React.Fragment key={it.key}>
          {i > 0 && <span className="px-1.5 text-white/25">·</span>}
          <span className="font-medium text-white/80">{it.key}</span> {it.label}
        </React.Fragment>
      ))}
    </p>
  );
}

function ToolbarDivider(): React.JSX.Element {
  return <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-white/[0.12]" />;
}

/**
 * One toolbar icon button with a neutral hover/focus tooltip (mirrors
 * the attachment-info popover shape, neutral palette).
 */
function PreviewToolButton(props: {
  label: string;
  shortcut?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  /** Icon tint: danger (red, Stop) / success (green, Play). Default
   *  is the neutral white used by Select / Reload. */
  tone?: 'danger' | 'success';
  testid: string;
  ariaLabel: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const toneClass = props.active
    ? // Selected state matches the chat composer's Send button: a translucent
      // brand (Canary) circle with the Canary icon.
      'bg-crucible-accent/20 text-crucible-accent-hover hover:bg-crucible-accent/30 hover:text-white'
    : props.tone === 'danger'
      ? 'text-red-400 hover:bg-white/[0.08] hover:text-red-300'
      : props.tone === 'success'
        ? 'text-emerald-400 hover:bg-white/[0.08] hover:text-emerald-300'
        : 'text-white/70 hover:bg-white/[0.08] hover:text-white';
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={props.onClick}
        disabled={props.disabled}
        aria-label={props.ariaLabel}
        aria-pressed={props.active}
        data-testid={props.testid}
        className={cn(
          'peer app-region-no-drag flex h-10 w-10 items-center justify-center rounded-full transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-40',
          toneClass,
        )}
      >
        {props.children}
      </button>
      {/* Standardised design-system tooltip. Opens DOWNWARD — the toolbar
          sits at the top of the preview, so an upward tooltip would be
          clipped by the header. Shows on hover + KEYBOARD focus only. */}
      <Tooltip side="bottom">
        <span className="min-w-0">{props.label}</span>
        {props.shortcut && <TooltipKbd>{props.shortcut}</TooltipKbd>}
      </Tooltip>
    </span>
  );
}

function DisplayPane(props: {
  url?: string;
  reloadKey: number;
  loading?: boolean;
  /** Height (px) reserved below the box for the mobile-screen strip so the
   *  16:9 display letterboxes against the remaining height (keeps the
   *  display + strip a tight group). 0 = use the full container height. */
  reservePx?: number;
  displayIframeRef?: React.Ref<HTMLIFrameElement>;
  onDisplayIframeLoad?: (url: string) => void;
}): React.JSX.Element {
  const reserve = props.reservePx ?? 0;
  // Keep the 16:9 box letterboxed against the BINDING dimension in BOTH
  // orientations. Width is the smaller of "fill the container width" and
  // "the width a (container-height − strip-reserve) tall 16:9 box needs";
  // maxHeight leaves the reserved strip room. The parent display-area owns
  // `container-type: size`, so `100cqh` is its height. A single-axis
  // `h-full`/`w-full` bind would lose 16:9 on the other axis.
  return (
    <div
      className="relative shrink-0 overflow-hidden"
      style={{
        aspectRatio: '16 / 9',
        width:
          reserve > 0
            ? `min(100%, calc((100cqh - ${reserve}px) * 16 / 9))`
            : 'min(100%, calc(100cqh * 16 / 9))',
        maxHeight: reserve > 0 ? `calc(100% - ${reserve}px)` : '100%',
        background: 'var(--surface-preview-pane)',
      }}
    >
      {props.url ? (
        <>
          <iframe
            ref={props.displayIframeRef}
            key={`display-${props.reloadKey}`}
            src={props.url}
            title="Display preview"
            data-testid="preview-iframe-display"
            // Select-to-chat — surface tag the inspector hook reads to
            // attribute selections + label the context block.
            data-crucible-surface="tv"
            className="absolute inset-0 h-full w-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms"
            allow="autoplay"
            onLoad={() => props.onDisplayIframeLoad?.(props.url!)}
          />
          <span className="sr-only">{props.url}</span>
        </>
      ) : (
        <NewUiIdlePane label="TV" loading={props.loading} />
      )}
    </div>
  );
}

/**
 * Horizontal strip of small mobile screens, shown BELOW the TV. Each
 * phone is a thumbnail that focuses it (shown large in the controllers
 * column) and carries a ✕ to remove that specific phone; a trailing
 * dashed [+] slot adds a phone. Replaces the old in-column strip + the
 * separate [+ phone] / [− remove] buttons.
 */
function PreviewPhoneStrip(props: {
  urls: string[];
  focusedIndex: number | null;
  setFocusedPaneIndex: (idx: number | null) => void;
  onAddPhone?: () => void;
  onRemovePhoneAt?: (index: number) => void | Promise<boolean>;
  maxControllerSlots: number;
}): React.JSX.Element | null {
  const { urls, focusedIndex } = props;
  if (urls.length === 0) return null;
  const canAdd = !!props.onAddPhone && urls.length < props.maxControllerSlots;
  const canRemove = !!props.onRemovePhoneAt && urls.length > 1;
  const nextAddSlot = urls.length + 1;
  const addDisabledTitle =
    props.maxControllerSlots === 1
      ? 'This plan is set to 1 player'
      : urls.length >= props.maxControllerSlots
        ? `All ${props.maxControllerSlots} player slots are connected`
        : undefined;

  const handleRemove = (idx: number): void => {
    const remove = props.onRemovePhoneAt;
    if (!remove || urls.length <= 1) return;
    const shiftFocus = (): void => {
      // Keep the same phone focused as the array shifts under the removal.
      if (focusedIndex === null) return;
      if (idx < focusedIndex) props.setFocusedPaneIndex(focusedIndex - 1);
      else if (idx === focusedIndex)
        props.setFocusedPaneIndex(Math.max(0, Math.min(focusedIndex, urls.length - 2)));
    };
    // `onRemovePhoneAt` resolves `false` when the main process rejects the
    // removal (the URL list is left untouched). Only shift focus once the
    // removal has actually committed — otherwise a failed remove strands
    // focus on the wrong (still-connected) phone. A `void`-returning handler
    // (test stubs) is treated as success so the optimistic shift is kept.
    Promise.resolve(remove(idx)).then(
      (ok) => {
        if (ok !== false) shiftFocus();
      },
      () => {
        /* swallow — focus stays put on rejection */
      },
    );
  };

  return (
    <div data-testid="preview-phone-strip" className="flex shrink-0 flex-col">
      {/* `pt-1.5` gives the hover lift (-translate-y) headroom so the top
          edge of a thumbnail isn't clipped by the row's overflow. */}
      <div className="flex items-stretch justify-center gap-1.5 overflow-x-auto px-1 pt-1.5">
        {urls.map((_url, idx) => {
          const isFocused = idx === focusedIndex;
          const colour = getPaneColour(idx);
          return (
            <div key={idx} className="relative shrink-0" style={{ width: 44 }}>
              <button
                type="button"
                onClick={() => props.setFocusedPaneIndex(idx)}
                data-testid={
                  isFocused
                    ? `preview-phone-focused-placeholder-${idx}`
                    : `preview-phone-focus-${idx}`
                }
                data-pane-index={idx}
                data-size={isFocused ? 'focused-placeholder' : 'thumbnail'}
                aria-label={isFocused ? `Phone ${idx + 1} is focused` : `Focus phone ${idx + 1}`}
                aria-pressed={isFocused}
                className="app-region-no-drag relative block w-full overflow-hidden rounded-lg bg-black/20 transition-transform hover:-translate-y-0.5"
                style={{
                  aspectRatio: '9 / 19.5',
                  // Selected phone is highlighted with the brand GRADIENT
                  // (Canary → warmed Clementine), matching the primary
                  // button — not the per-player colour. A gradient border on
                  // a rounded box needs the padding-box/border-box layering
                  // trick (a plain `border` can't take a gradient and
                  // `border-image` ignores the radius).
                  ...(isFocused
                    ? {
                        border: '2px solid transparent',
                        background:
                          'linear-gradient(rgba(255,255,255,0.04), rgba(255,255,255,0.04)) padding-box, var(--gradient-brand) border-box',
                      }
                    : { border: '1px solid rgba(255,255,255,0.10)' }),
                }}
              >
                <PhoneStripPreviewCard colour={colour} />
              </button>
              {canRemove && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(idx);
                  }}
                  data-testid={`preview-phone-remove-${idx}`}
                  aria-label={`Remove phone ${idx + 1}`}
                  title={`Remove phone ${idx + 1}`}
                  className="app-region-no-drag absolute right-0.5 top-0.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-black/65 text-white/75 transition-colors hover:bg-red-500/85 hover:text-white"
                >
                  <X size={10} strokeWidth={2.5} />
                </button>
              )}
            </div>
          );
        })}
        {props.onAddPhone && (
          <button
            type="button"
            onClick={canAdd ? props.onAddPhone : undefined}
            disabled={!canAdd}
            data-testid="preview-add-phone-button"
            aria-label="Add a phone"
            title={addDisabledTitle ?? `Connect P${nextAddSlot}`}
            className="app-region-no-drag flex shrink-0 items-center justify-center rounded-lg border border-dashed border-white/20 text-white/45 transition-colors hover:border-white/40 hover:text-white/80 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ width: 44, aspectRatio: '9 / 19.5' }}
          >
            <Plus size={18} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
}

function ControllersRow(props: {
  urls: string[];
  pairing?: DevSessionPairingMetadata;
  previewMode?: PreviewMode;
  reloadKey: number;
  loading?: boolean;
  embedded?: boolean;
  focusedPaneIndex: number | null;
  setFocusedPaneIndex: (idx: number | null) => void;
  onAddPhone?: () => void;
  onRemovePhone?: () => void;
  /** Remove a specific phone by index (per-screen ✕ on the phone strip). */
  onRemovePhoneAt?: (index: number) => void | Promise<boolean>;
  maxControllerSlots?: number;
  previewPhoneCapOverride?: number | null;
  onPreviewPhoneCapChange?: (value: number | null) => void | Promise<void>;
  playerCountLabel?: string;
  playerCountSource?: 'quickAnswers' | 'slotAnswers' | 'unknown';
  diagnostics?: FoundryPreviewDevState | null;
}): React.JSX.Element {
  const maxControllerSlots = Math.max(
    1,
    Math.min(MAX_PHONES_IN_OVERLAY, Math.floor(props.maxControllerSlots ?? MAX_PHONES_IN_OVERLAY)),
  );
  const effectiveFocused =
    props.urls.length > 0 ? Math.min(props.focusedPaneIndex ?? 0, props.urls.length - 1) : null;
  const showPairedPreviewUi = isPairedPreviewUi(props.previewMode, props.pairing);
  // Stop / Reload / Restart moved out of here into the floating
  // PreviewToolbar below the TV (design 2026-06-13).
  return (
    <div
      className={
        props.embedded
          ? 'flex min-h-[220px] min-w-0 flex-[852] flex-col overflow-hidden p-4 pt-0 lg:min-h-0 lg:w-[var(--preview-ctrl-w,360px)] lg:min-w-[240px] lg:flex-none lg:p-4 lg:pl-0'
          : 'flex min-h-[220px] min-w-0 flex-[852] flex-col overflow-hidden p-4 pt-0 lg:min-h-0 lg:w-[var(--preview-ctrl-w,380px)] lg:min-w-[260px] lg:flex-none lg:p-4 lg:pl-0'
      }
      data-testid="preview-pane-controllers"
    >
      {/* The inline preview-diagnostics health readout was removed from the
          controllers column — it sat above the focused phone and stole its
          vertical height (squeezing/cutting the phone). The diagnostics data
          still feeds the pairing panel + the dev-loop banner's health states. */}
      {showPairedPreviewUi && (
        <div className="flex min-h-0 flex-1">
          <PairingPanel
            pairing={props.pairing}
            diagnostics={props.diagnostics}
            compact
            maxControllerSlots={maxControllerSlots}
          />
        </div>
      )}
      {!showPairedPreviewUi && (
        <>
          {/* Always show slot 0 — idle placeholder when no URL yet. */}
          {props.urls.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <ControllerSlot
                url={undefined}
                reloadKey={props.reloadKey}
                index={0}
                loading={props.loading}
              />
            </div>
          ) : (
            <>
              <div
                data-testid="preview-pane-controller"
                data-pane-index={effectiveFocused ?? 0}
                data-size="focused"
                className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden"
              >
                <PersistentPhoneStage
                  urls={props.urls}
                  focusedIndex={effectiveFocused ?? 0}
                  reloadKey={props.reloadKey}
                  loading={props.loading}
                />
              </div>
              {/* The phone strip + add/remove affordances moved out of this
                  column to a horizontal strip BELOW the TV (PreviewPhoneStrip
                  in NewUiPreviewBody). This column now shows only the focused
                  phone. */}
            </>
          )}
        </>
      )}
    </div>
  );
}

function DiagnosticLine(props: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="grid min-w-0 grid-cols-[72px_minmax(0,1fr)] gap-2">
      <span className="text-white/35">{props.label}</span>
      <span className="truncate font-mono text-white/62" title={props.value}>
        {props.value}
      </span>
    </div>
  );
}

function PairingPanel(props: {
  pairing?: DevSessionPairingMetadata;
  diagnostics?: FoundryPreviewDevState | null;
  compact?: boolean;
  maxControllerSlots?: number;
}): React.JSX.Element {
  const hasTunnelMetadata = props.pairing !== undefined;
  const urlDiagnostics = props.diagnostics?.urlDiagnostics;
  const publicJoinUrl = props.pairing?.controllerBaseUrl ?? urlDiagnostics?.controllerBaseUrl;
  const warning = (urlDiagnostics?.warnings ?? []).find(
    (entry) =>
      entry.code === 'paired-physical-phone-loopback-target' ||
      entry.code === 'paired-public-origin-mismatch',
  );
  const effectiveMaxControllerSlots = Math.max(
    1,
    Math.min(MAX_PHONES_IN_OVERLAY, Math.floor(props.maxControllerSlots ?? MAX_PHONES_IN_OVERLAY)),
  );
  return (
    <div
      data-testid="preview-pairing-panel"
      className={
        props.compact
          ? 'flex min-h-0 flex-1 flex-col justify-between rounded-lg border border-sky-300/20 bg-sky-300/10 p-3 text-xs text-sky-50/85'
          : 'flex min-h-0 flex-col justify-between rounded-lg border border-sky-300/20 bg-sky-300/10 p-4 text-xs text-sky-50/85'
      }
    >
      <div className="grid gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-100/55">
            Weekend app pairing
          </div>
          <div className="mt-1 text-sm font-semibold text-sky-50">Weekend app pairing active</div>
          <div
            data-testid="preview-pairing-phone-support"
            className="mt-1 text-[11px] text-sky-50/70"
          >
            Game supports up to {formatPhoneWord(effectiveMaxControllerSlots)}
          </div>
          <div className="mt-1 text-[11px] text-sky-50/55">
            {hasTunnelMetadata ? 'Public tunnel active' : 'Tunnel metadata unavailable'}
          </div>
        </div>
        <div className="grid gap-1.5">
          <DiagnosticLine
            label="Host"
            value={props.pairing?.publicHost ?? PAIRING_METADATA_UNAVAILABLE}
          />
          <DiagnosticLine
            label="Tunnel"
            value={props.pairing?.publicTunnelOrigin ?? PAIRING_METADATA_UNAVAILABLE}
          />
          <DiagnosticLine
            label="Controller"
            value={props.pairing?.controllerBaseUrl ?? PAIRING_METADATA_UNAVAILABLE}
          />
          <DiagnosticLine label="Join URL" value={publicJoinUrl ?? PAIRING_METADATA_UNAVAILABLE} />
        </div>
        {warning && (
          <div
            data-testid="preview-pairing-warning"
            className="rounded-md border border-amber-300/25 bg-amber-300/12 px-2.5 py-2 text-[11px] leading-snug text-amber-100/90"
            title={warning.url}
          >
            {warning.message}
          </div>
        )}
      </div>
      <div className="mt-4 rounded-md border border-white/10 bg-black/20 px-2.5 py-2 text-[11px] leading-snug text-sky-50/70">
        Stop Preview or quit Foundry to close this tunnel.
      </div>
    </div>
  );
}

function PersistentPhoneStage(props: {
  urls: string[];
  focusedIndex: number;
  reloadKey: number;
  loading?: boolean;
}): React.JSX.Element {
  // `container-type: size` here is the query container the focused PhoneFrame
  // letterboxes against (mirrors the TV's display-area). Without it the phone
  // box can't fit to BOTH axes and overflows its column (clipped/scrolls).
  if (props.urls.length === 0) {
    return (
      <div
        className="relative flex h-full w-full items-center justify-center"
        style={{ containerType: 'size' }}
      >
        <PhoneFrame
          url={undefined}
          index={0}
          reloadKey={props.reloadKey}
          loading={props.loading}
          focused
        />
      </div>
    );
  }
  return (
    <div className="relative h-full w-full" style={{ containerType: 'size' }}>
      {props.urls.map((url, index) => {
        const isFocused = index === props.focusedIndex;
        return (
          <div
            key={url}
            data-testid={`preview-phone-frame-slot-${index}`}
            aria-hidden={isFocused ? undefined : 'true'}
            className="absolute inset-0 flex items-center justify-center"
            style={{
              opacity: isFocused ? 1 : 0,
              visibility: isFocused ? 'visible' : 'hidden',
              pointerEvents: isFocused ? 'auto' : 'none',
            }}
          >
            <PhoneFrame
              url={url}
              index={index}
              reloadKey={props.reloadKey}
              loading={props.loading}
              focused
            />
          </div>
        );
      })}
    </div>
  );
}

function PhoneStripPreviewCard(props: { colour: string }): React.JSX.Element {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-[#0a0813]">
      <div
        className="flex h-full w-full items-center justify-center"
        style={{ color: props.colour, background: 'rgba(255,255,255,0.035)' }}
      >
        <span
          aria-hidden="true"
          className="block rounded-full"
          style={{
            width: 8,
            height: 8,
            background: props.colour,
            boxShadow: `0 0 0 4px ${props.colour}1f`,
          }}
        />
      </div>
    </div>
  );
}

function PhoneFrame(props: {
  url?: string;
  reloadKey: number;
  index: number;
  loading?: boolean;
  focused?: boolean;
  thumbnail?: boolean;
}): React.JSX.Element {
  return (
    <div
      className="relative max-h-full max-w-full overflow-hidden"
      style={{
        aspectRatio: '9 / 19.5',
        // Letterbox to fit BOTH axes (same pattern as the TV): width is the
        // smaller of the column width and the width a full-height 9:19.5 box
        // needs; maxHeight caps it so it never overflows the column. The
        // `100cqh` resolves against the PersistentPhoneStage size container.
        width: props.thumbnail ? '100%' : 'min(100%, calc(100cqh * 9 / 19.5))',
        maxHeight: '100%',
        background: 'var(--surface-preview-pane)',
        containerType: 'size',
      }}
    >
      {props.url ? (
        <>
          <iframe
            key={`controller-${props.index}-${props.reloadKey}`}
            src={props.url}
            title={`Phone ${props.index + 1} preview`}
            data-testid={`preview-iframe-phone-${props.index}`}
            // Select-to-chat — surface tag (thumbnails get pointer-events
            // none below, so only the focused phone is selectable).
            data-crucible-surface={`controller:${props.index}`}
            className={props.focused ? undefined : 'absolute inset-0 h-full w-full border-0'}
            sandbox="allow-scripts allow-same-origin allow-forms"
            allow="microphone; gyroscope; accelerometer; autoplay"
            style={
              props.focused || props.thumbnail
                ? {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 390,
                    height: 844,
                    border: 0,
                    pointerEvents: props.thumbnail ? 'none' : undefined,
                    transform: 'scale(min(calc(100cqw / 390px), calc(100cqh / 844px)))',
                    transformOrigin: 'top left',
                  }
                : undefined
            }
          />
          <span className="sr-only">{props.url}</span>
        </>
      ) : (
        <NewUiIdlePane label="Phone" loading={props.loading} />
      )}
    </div>
  );
}

function ControllerSlot(props: {
  url?: string;
  reloadKey: number;
  index: number;
  loading?: boolean;
  onRemove?: () => void;
}): React.JSX.Element {
  return (
    <div
      className="group relative max-h-full max-w-full overflow-hidden"
      style={{
        aspectRatio: '9/19.5',
        height: '100%',
        background: 'var(--surface-preview-pane)',
      }}
      data-testid={
        props.index === 0 ? 'preview-pane-controller' : `preview-pane-controller-${props.index}`
      }
    >
      {props.url ? (
        <>
          <iframe
            key={`controller-${props.index}-${props.reloadKey}`}
            src={props.url}
            title="Controller preview"
            data-testid="preview-iframe-controller"
            data-crucible-surface={`controller:${props.index}`}
            className="absolute inset-0 h-full w-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms"
            allow="microphone; gyroscope; accelerometer; autoplay"
          />
          <span className="sr-only">{props.url}</span>
        </>
      ) : (
        <NewUiIdlePane label="Phone" loading={props.loading} />
      )}
      {props.onRemove && (
        <button
          type="button"
          onClick={props.onRemove}
          aria-label="Remove phone"
          className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M1 1l8 8M9 1L1 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

function NewUiIdlePane(props: { label: string; loading?: boolean }): React.JSX.Element {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1">
      {props.loading ? (
        <LoadingSpinner />
      ) : (
        <p className="text-[11px] uppercase tracking-widest text-white/20">{props.label}</p>
      )}
    </div>
  );
}

// ─── Shared glyphs ───────────────────────────────────────────────────────────

function PreviewErrorCard(props: {
  error: SerialisedError;
  onSendToChat?: (error: SerialisedError) => void;
  onDismiss?: () => void;
  onRetryPreview?: () => void;
  showBackButton?: boolean;
}): React.JSX.Element {
  return (
    <div
      className="relative flex h-full w-full items-center justify-center"
      data-testid="preview-overlay-error"
    >
      {props.showBackButton !== false && props.onDismiss && (
        <button
          type="button"
          onClick={props.onDismiss}
          data-testid="preview-overlay-back"
          aria-label="Back to Build"
          title="Close preview and return to Build (Esc)"
          className="app-region-no-drag absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-3 py-2 text-xs font-medium text-white/75 transition-colors hover:border-white/25 hover:bg-white/10 hover:text-white"
        >
          <BackArrowGlyph />
          Back
        </button>
      )}
      <div className="w-full max-w-xl">
        <CrucibleErrorState error={props.error} />
        <div className="mt-4 flex items-center gap-2">
          {props.onRetryPreview && (
            <button
              type="button"
              onClick={props.onRetryPreview}
              data-testid="preview-overlay-error-retry"
              className="rounded-md border border-crucible-accent/40 bg-crucible-accent/10 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:border-crucible-accent/60 hover:bg-crucible-accent/20"
            >
              Retry preview
            </button>
          )}
          {props.onSendToChat && (
            <button
              type="button"
              onClick={() => props.onSendToChat?.(props.error)}
              data-testid="preview-overlay-error-send-to-chat"
              className="rounded-md border border-crucible-accent/40 bg-crucible-accent/10 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:border-crucible-accent/60 hover:bg-crucible-accent/20"
            >
              Send to Build for help
            </button>
          )}
          {props.onDismiss && (
            <button
              type="button"
              onClick={props.onDismiss}
              data-testid="preview-overlay-error-dismiss"
              className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition-colors hover:border-white/30 hover:bg-white/10 hover:text-white"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner(): React.JSX.Element {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="mx-auto animate-spin text-white/40"
      aria-hidden="true"
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

function BackArrowGlyph(): React.JSX.Element {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}
